import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import DateSelect from '../components/ui/DateSelect';
import SearchableSelect from '../components/ui/SearchableSelect';
import { useStudentStore } from '../store/StudentStore';
import { useUser } from '../store/UserContext';
import { formatDateTime } from '../utils/dateFormat';
import StaffService from '../services/StaffService';

const itemTypes = [
  'Hardware',
  'Sports Equipment',
  'Tech Equipment',
  'Software License',
  'Learning Materials',
  'Other'
];

const deviceTypeHints = ['tech', 'device', 'phone', 'tablet', 'mobile', 'electronics', 'laptop'];
const imageTypes = ['image/jpeg', 'image/png', 'image/webp'];
const invoiceTypes = ['application/pdf', ...imageTypes];
const maxImageSize = 5 * 1024 * 1024;
const maxInvoiceSize = 10 * 1024 * 1024;

// TODO(API): Replace with upload flow; storing metadata only for now.
const buildFileMeta = (file) => ({
  id: `file-${Math.random().toString(36).slice(2, 9)}`,
  name: file.name,
  size: file.size,
  type: file.type,
  uploadedAt: new Date().toISOString()
});

const normalizeImei = (value) => value.replace(/\s+/g, '');
const safeList = (service) => {
  if (service && typeof service.list === 'function') return service.list();
  return [];
};
const createPreviewUrl = (file) => {
  if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
    return URL.createObjectURL(file);
  }
  return 'data:application/octet-stream;base64,';
};
const revokePreviewUrl = (value) => {
  if (!value) return;
  if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(value);
  }
};

export default function InventoryCreatePage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { inventoryController, inventoryItems, students } = useStudentStore();
  const staffService = useMemo(() => new StaffService(), []);
  const staffList = useMemo(() => safeList(staffService), [staffService]);
  const isAdmin = ['admin', 'manager'].includes((user?.role || '').toLowerCase());

  const [overrideAssetTag, setOverrideAssetTag] = useState(false);
  const [form, setForm] = useState({
    name: '',
    type: 'Tech Equipment',
    description: '',
    notes: '',
    reference: '',
    serialNumber: '',
    imei: '',
    condition: 'Good',
    conditionNotes: '',
    purchaseDate: '',
    purchaseCost: '',
    warrantyExpiry: '',
    location: '',
    safeguardingRelevant: false,
    loanable: false,
    assignmentMode: 'unassigned',
    assignedToType: 'student',
    assignedToId: '',
    assignmentDate: new Date().toISOString().slice(0, 10),
    dueDate: '',
    purpose: '',
    notesLog: '',
    serviceContact: {
      name: '',
      phone: '',
      email: '',
      url: ''
    }
  });
  const [errors, setErrors] = useState({});
  const [serialWarning, setSerialWarning] = useState('');
  const [imeiWarning, setImeiWarning] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [invoiceFile, setInvoiceFile] = useState(null);

  const assignmentOptions = useMemo(() => {
    const list = form.assignmentMode === 'student' ? students : staffList;
    return list.map((person) => ({
      value: person.id,
      label: person.name,
      meta: form.assignmentMode === 'student'
        ? person.reference || person.id
        : person.role || person.title || ''
    }));
  }, [form.assignmentMode, staffList, students]);

  const isDeviceType = useMemo(() => {
    const value = form.type.toLowerCase();
    return deviceTypeHints.some((hint) => value.includes(hint));
  }, [form.type]);

  useEffect(() => {
    if (!isDeviceType && form.imei) {
      setForm((prev) => ({ ...prev, imei: '' }));
    }
  }, [isDeviceType, form.imei]);

  useEffect(() => {
    if (!form.serialNumber.trim()) {
      setSerialWarning('');
      return;
    }
    const exists = inventoryItems.some(
      (item) => item.serialNumber && item.serialNumber.toLowerCase() === form.serialNumber.trim().toLowerCase()
    );
    setSerialWarning(exists ? 'Serial number already exists in inventory.' : '');
  }, [form.serialNumber, inventoryItems]);

  useEffect(() => {
    if (!form.imei.trim()) {
      setImeiWarning('');
      return;
    }
    const normalized = normalizeImei(form.imei);
    const exists = inventoryItems.some(
      (item) => item.imei && normalizeImei(item.imei) === normalized
    );
    setImeiWarning(exists ? 'IMEI already exists in inventory.' : '');
  }, [form.imei, inventoryItems]);

  useEffect(() => {
    return () => {
      imageFiles.forEach((image) => revokePreviewUrl(image.preview));
    };
  }, [imageFiles]);

  const validateForm = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Item name is required.';
    if (!form.type.trim()) next.type = 'Category/type is required.';
    if (overrideAssetTag && !form.reference.trim()) next.reference = 'Asset tag is required.';
    if (form.assignmentMode !== 'unassigned' && !form.assignedToId) {
      next.assignedToId = 'Select a student or staff member.';
    }
    if (form.assignmentMode !== 'unassigned' && form.loanable && !form.dueDate) {
      next.dueDate = 'Due date is required for loan items.';
    }
    if (form.imei.trim()) {
      const normalized = normalizeImei(form.imei);
      if (!/^\d+$/.test(normalized)) {
        next.imei = 'IMEI must contain digits only.';
      } else if (normalized.length < 14 || normalized.length > 16) {
        next.imei = 'IMEI must be 14 to 16 digits.';
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleAddImages = (files) => {
    const accepted = [];
    const invalid = [];
    [...files].forEach((file) => {
      if (!imageTypes.includes(file.type)) {
        invalid.push(`${file.name} (unsupported type)`);
        return;
      }
      if (file.size > maxImageSize) {
        invalid.push(`${file.name} (exceeds 5MB)`);
        return;
      }
      accepted.push({
        file,
        preview: createPreviewUrl(file)
      });
    });
    if (invalid.length) {
      setErrors((prev) => ({ ...prev, images: `Some files were not added: ${invalid.join(', ')}` }));
    } else {
      setErrors((prev) => ({ ...prev, images: '' }));
    }
    if (accepted.length) {
      setImageFiles((prev) => [...prev, ...accepted]);
    }
  };

  const removeImage = (index) => {
    setImageFiles((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      revokePreviewUrl(removed?.preview);
      return next;
    });
  };

  const handleInvoiceChange = (file) => {
    if (!file) return;
    if (!invoiceTypes.includes(file.type)) {
      setErrors((prev) => ({ ...prev, invoice: 'Invoice must be a PDF or image.' }));
      return;
    }
    if (file.size > maxInvoiceSize) {
      setErrors((prev) => ({ ...prev, invoice: 'Invoice exceeds 10MB.' }));
      return;
    }
    setErrors((prev) => ({ ...prev, invoice: '' }));
    setInvoiceFile(file);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const assigned = form.assignmentMode !== 'unassigned';
    const assignedToName =
      assigned && form.assignedToType === 'student'
        ? students.find((student) => student.id === form.assignedToId)?.name
        : staffList.find((staff) => staff.id === form.assignedToId)?.name;

    const assignments = assigned
      ? [
        {
          assignedToType: form.assignedToType,
          assignedToId: form.assignedToId,
          assignedToName: assignedToName || '',
          checkoutDate: form.assignmentDate,
          dueDate: form.dueDate || null,
          expectedReturnDate: form.dueDate || null,
          checkinDate: null,
          notes: form.purpose || ''
        }
      ]
      : [];

    const createdItem = await Promise.resolve(inventoryController.createItem(
      {
        name: form.name.trim(),
        type: form.type,
        description: form.description.trim(),
        reference: overrideAssetTag && form.reference.trim() ? form.reference.trim() : undefined,
        serialNumber: form.serialNumber.trim(),
        imei: form.imei.trim(),
        condition: form.condition,
        conditionNotes: form.conditionNotes.trim(),
        notes: form.notes.trim(),
        status: assigned ? 'checked_out' : 'available',
        assignments,
        images: imageFiles.map((image) => buildFileMeta(image.file)),
        invoiceFile: invoiceFile ? buildFileMeta(invoiceFile) : null,
        purchaseDate: form.purchaseDate,
        purchaseCost: form.purchaseCost,
        warrantyExpiry: form.warrantyExpiry,
        location: form.location.trim(),
        safeguardingRelevant: form.safeguardingRelevant,
        loanable: form.loanable,
        addedBy: user?.name || '',
        notesLog: form.notesLog.trim()
          ? [
            {
              id: `note-${Math.random().toString(36).slice(2, 9)}`,
              note: form.notesLog.trim(),
              createdAt: new Date().toISOString(),
              createdBy: user?.name || 'System'
            }
          ]
          : [],
        serviceContact: {
          name: form.serviceContact.name.trim(),
          phone: form.serviceContact.phone.trim(),
          email: form.serviceContact.email.trim(),
          url: form.serviceContact.url.trim()
        }
      },
      user
    ));

    navigate(createdItem?.id ? `/inventory/${createdItem.id}` : '/inventory', { state: { created: true } });
  };

  return (
    <Card className="sp-form">
      <div className="p-6 space-y-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '18px', fontWeight: 600 }}>Add Inventory Item</h2>
          <p className="text-sm sp-muted">Capture item details, condition, and assignment in one workflow.</p>
        </div>

        <section className="sp-form-section">
          <div className="sp-form-section__header">
            <h3 className="sp-value">Item details</h3>
            <p className="text-sm sp-muted">Core information and description.</p>
          </div>
          <div className="sp-form-grid">
            <div>
              <label className="sp-label" htmlFor="inv-name">Item name</label>
              <input
                id="inv-name"
                className="sp-input w-full"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              />
              {errors.name && <p className="sp-field-error">{errors.name}</p>}
            </div>
            <div>
              <label className="sp-label" htmlFor="inv-type">Category / type</label>
              <select
                id="inv-type"
                className="sp-select w-full"
                value={form.type}
                onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
              >
                {itemTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {errors.type && <p className="sp-field-error">{errors.type}</p>}
            </div>
            <div className="md:col-span-2">
              <label className="sp-label" htmlFor="inv-description">Description</label>
              <textarea
                id="inv-description"
                className="sp-textarea w-full"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="md:col-span-2">
              <label className="sp-label" htmlFor="inv-notes">Internal notes</label>
              <textarea
                id="inv-notes"
                className="sp-textarea w-full"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
        </section>

        <section className="sp-form-section">
          <div className="sp-form-section__header">
            <h3 className="sp-value">Identification</h3>
            <p className="text-sm sp-muted">Track asset tags and device IDs.</p>
          </div>
          <div className="sp-form-grid">
            <div>
              <label className="sp-label" htmlFor="inv-asset-tag">Asset tag / Inventory ID</label>
              {isAdmin ? (
                <>
                  <label className="sp-inline-toggle">
                    <input
                      type="checkbox"
                      checked={overrideAssetTag}
                      onChange={(event) => setOverrideAssetTag(event.target.checked)}
                    />
                    Override auto-generated tag
                  </label>
                  <input
                    id="inv-asset-tag"
                    className="sp-input w-full mt-2"
                    value={form.reference}
                    onChange={(event) => setForm((prev) => ({ ...prev, reference: event.target.value }))}
                    placeholder="Auto-generated if blank"
                    disabled={!overrideAssetTag}
                  />
                </>
              ) : (
                <input id="inv-asset-tag" className="sp-input w-full" value="Auto-generated" disabled />
              )}
              {errors.reference && <p className="sp-field-error">{errors.reference}</p>}
            </div>
            <div>
              <label className="sp-label" htmlFor="inv-serial">Serial number</label>
              <input
                id="inv-serial"
                className="sp-input w-full"
                value={form.serialNumber}
                onChange={(event) => setForm((prev) => ({ ...prev, serialNumber: event.target.value }))}
              />
              <p className="sp-field-help">Usually found on device labels or packaging.</p>
              {serialWarning && <p className="sp-field-warning">{serialWarning}</p>}
            </div>
            {isDeviceType && (
              <div>
                <label className="sp-label" htmlFor="inv-imei">IMEI (device only)</label>
                <input
                  id="inv-imei"
                  className="sp-input w-full"
                  value={form.imei}
                  onChange={(event) => setForm((prev) => ({ ...prev, imei: event.target.value }))}
                  placeholder="15 digit number"
                />
                <p className="sp-field-help">Dial *#06# to view IMEI. Spaces are allowed.</p>
                {errors.imei && <p className="sp-field-error">{errors.imei}</p>}
                {imeiWarning && <p className="sp-field-warning">{imeiWarning}</p>}
              </div>
            )}
          </div>
        </section>

        <section className="sp-form-section">
          <div className="sp-form-section__header">
            <h3 className="sp-value">Condition & images</h3>
            <p className="text-sm sp-muted">Record condition and attach photos.</p>
          </div>
          <div className="sp-form-grid">
            <div>
              <label className="sp-label" htmlFor="inv-condition">Condition</label>
              <select
                id="inv-condition"
                className="sp-select w-full"
                value={form.condition}
                onChange={(event) => setForm((prev) => ({ ...prev, condition: event.target.value }))}
              >
                <option value="Excellent">Excellent</option>
                <option value="Good">Good</option>
                <option value="Fair">Fair</option>
                <option value="Poor">Poor</option>
              </select>
            </div>
            <div>
              <label className="sp-label" htmlFor="inv-condition-notes">Condition notes</label>
              <textarea
                id="inv-condition-notes"
                className="sp-textarea w-full"
                rows={3}
                value={form.conditionNotes}
                onChange={(event) => setForm((prev) => ({ ...prev, conditionNotes: event.target.value }))}
              />
            </div>
            <div className="md:col-span-2">
              <label className="sp-label" htmlFor="inv-images">Image uploads</label>
              <input
                id="inv-images"
                type="file"
                className="sp-input w-full"
                multiple
                accept={imageTypes.join(',')}
                onChange={(event) => handleAddImages(event.target.files)}
              />
              <p className="sp-field-help">Upload JPG, PNG, or WebP images (max 5MB each).</p>
              {errors.images && <p className="sp-field-error">{errors.images}</p>}
              {imageFiles.length > 0 && (
                <div className="sp-image-grid">
                  {imageFiles.map((image, index) => (
                    <div key={image.preview} className="sp-image-tile">
                      <img src={image.preview} alt={`Upload ${index + 1}`} />
                      <button type="button" onClick={() => removeImage(index)}>Remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="sp-form-section">
          <div className="sp-form-section__header">
            <h3 className="sp-value">Purchase & service</h3>
            <p className="text-sm sp-muted">Capture procurement and support information.</p>
          </div>
          <div className="sp-form-grid">
            <div>
              <label className="sp-label" htmlFor="inv-purchase-date">Purchase date</label>
              <DateSelect
                id="inv-purchase-date"
                className="w-full mt-2"
                value={form.purchaseDate}
                onChange={(value) => setForm((prev) => ({ ...prev, purchaseDate: value }))}
              />
            </div>
            <div>
              <label className="sp-label" htmlFor="inv-purchase-cost">Purchase cost (optional)</label>
              <input
                id="inv-purchase-cost"
                className="sp-input w-full"
                type="number"
                min="0"
                step="0.01"
                value={form.purchaseCost}
                onChange={(event) => setForm((prev) => ({ ...prev, purchaseCost: event.target.value }))}
              />
            </div>
            <div>
              <label className="sp-label" htmlFor="inv-warranty">Warranty expiry</label>
              <DateSelect
                id="inv-warranty"
                className="w-full mt-2"
                value={form.warrantyExpiry}
                onChange={(value) => setForm((prev) => ({ ...prev, warrantyExpiry: value }))}
              />
            </div>
            <div>
              <label className="sp-label" htmlFor="inv-invoice">Product invoice</label>
              <input
                id="inv-invoice"
                type="file"
                className="sp-input w-full"
                accept={invoiceTypes.join(',')}
                onChange={(event) => handleInvoiceChange(event.target.files?.[0])}
              />
              {errors.invoice && <p className="sp-field-error">{errors.invoice}</p>}
              {invoiceFile && (
                <div className="sp-file-pill">
                  <span>{invoiceFile.name}</span>
                  <button type="button" onClick={() => setInvoiceFile(null)}>Remove</button>
                </div>
              )}
            </div>
            <div>
              <label className="sp-label" htmlFor="inv-supplier">Supplier / vendor</label>
              <input
                id="inv-supplier"
                className="sp-input w-full"
                value={form.serviceContact.name}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    serviceContact: { ...prev.serviceContact, name: event.target.value }
                  }))
                }
              />
            </div>
            <div>
              <label className="sp-label" htmlFor="inv-support-phone">Support phone</label>
              <input
                type="tel"
                id="inv-support-phone"
                className="sp-input w-full"
                value={form.serviceContact.phone}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    serviceContact: { ...prev.serviceContact, phone: event.target.value }
                  }))
                }
              />
            </div>
            <div>
              <label className="sp-label" htmlFor="inv-support-email">Support email</label>
              <input
                type="email"
                id="inv-support-email"
                className="sp-input w-full"
                value={form.serviceContact.email}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    serviceContact: { ...prev.serviceContact, email: event.target.value }
                  }))
                }
              />
            </div>
            <div>
              <label className="sp-label" htmlFor="inv-support-url">Support URL</label>
              <input
                type="url"
                id="inv-support-url"
                className="sp-input w-full"
                value={form.serviceContact.url}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    serviceContact: { ...prev.serviceContact, url: event.target.value }
                  }))
                }
              />
            </div>
          </div>
        </section>

        <section className="sp-form-section">
          <div className="sp-form-section__header">
            <h3 className="sp-value">Assignment & location</h3>
            <p className="text-sm sp-muted">Define where the item lives and who it is assigned to.</p>
          </div>
          <div className="sp-form-grid">
            <div>
              <label className="sp-label" htmlFor="inv-location">Storage location</label>
              <input
                id="inv-location"
                className="sp-input w-full"
                value={form.location}
                onChange={(event) => setForm((prev) => ({ ...prev, location: event.target.value }))}
                placeholder="e.g., Office cupboard"
              />
            </div>
            <div>
              <label className="sp-label">Assignment status</label>
              <div className="sp-segment" role="tablist" aria-label="Assignment mode">
                {['unassigned', 'student', 'staff'].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    role="tab"
                    aria-selected={form.assignmentMode === mode}
                    className={form.assignmentMode === mode ? 'active' : ''}
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        assignmentMode: mode,
                        assignedToType: mode === 'staff' ? 'staff' : 'student',
                        assignedToId: ''
                      }))
                    }
                  >
                    {mode === 'unassigned' ? 'Unassigned' : mode === 'student' ? 'Student' : 'Staff'}
                  </button>
                ))}
              </div>
            </div>
            {form.assignmentMode !== 'unassigned' && (
              <>
                <div>
                  <label className="sp-label" htmlFor="inv-assigned-to">Assigned to</label>
                  <SearchableSelect
                    id="inv-assigned-to"
                    value={form.assignedToId}
                    options={assignmentOptions}
                    placeholder={`Search ${form.assignmentMode === 'student' ? 'students' : 'staff'}`}
                    emptyLabel={`No ${form.assignmentMode === 'student' ? 'students' : 'staff'} found.`}
                    ariaLabel="Search assignee"
                    onChange={(value) => setForm((prev) => ({ ...prev, assignedToId: value }))}
                  />
                  {errors.assignedToId && <p className="sp-field-error">{errors.assignedToId}</p>}
                </div>
                <div>
                  <label className="sp-label" htmlFor="inv-assignment-date">Assignment date</label>
                  <DateSelect
                    id="inv-assignment-date"
                    className="w-full mt-2"
                    value={form.assignmentDate}
                    onChange={(value) => setForm((prev) => ({ ...prev, assignmentDate: value }))}
                  />
                </div>
                <div>
                  <label className="sp-label">Loan item</label>
                  <label className="sp-inline-toggle">
                    <input
                      type="checkbox"
                      checked={form.loanable}
                      onChange={(event) => setForm((prev) => ({ ...prev, loanable: event.target.checked }))}
                    />
                    Require due date & track overdue
                  </label>
                </div>
                <div>
                  <label className="sp-label" htmlFor="inv-due-date">Due date {form.loanable ? '(required)' : '(optional)'}</label>
                  <DateSelect
                    id="inv-due-date"
                    className="w-full mt-2"
                    value={form.dueDate}
                    onChange={(value) => setForm((prev) => ({ ...prev, dueDate: value }))}
                  />
                  {errors.dueDate && <p className="sp-field-error">{errors.dueDate}</p>}
                </div>
                <div className="md:col-span-2">
                  <label className="sp-label" htmlFor="inv-purpose">Purpose / reason</label>
                  <input
                    id="inv-purpose"
                    className="sp-input w-full"
                    value={form.purpose}
                    onChange={(event) => setForm((prev) => ({ ...prev, purpose: event.target.value }))}
                    placeholder="Optional context for the assignment"
                  />
                </div>
              </>
            )}
          </div>
        </section>

        <section className="sp-form-section">
          <div className="sp-form-section__header">
            <h3 className="sp-value">Compliance & audit</h3>
            <p className="text-sm sp-muted">Track safeguarding relevance and notes.</p>
          </div>
          <div className="sp-form-grid">
            <div>
              <label className="sp-label" htmlFor="inv-safeguarding">Safeguarding relevance</label>
              <label className="sp-inline-toggle">
                <input
                  id="inv-safeguarding"
                  type="checkbox"
                  checked={form.safeguardingRelevant}
                  onChange={(event) => setForm((prev) => ({ ...prev, safeguardingRelevant: event.target.checked }))}
                />
                Requires tighter audit oversight
              </label>
            </div>
            <div>
              <label className="sp-label" htmlFor="inv-added-by">Added by</label>
              <input id="inv-added-by" className="sp-input w-full" value={user?.name || 'System'} disabled />
            </div>
            <div>
              <label className="sp-label" htmlFor="inv-created-at">Created at</label>
              <input id="inv-created-at" className="sp-input w-full" value={formatDateTime(new Date())} disabled />
            </div>
            <div className="md:col-span-2">
              <label className="sp-label" htmlFor="inv-log-entry">Initial log entry (optional)</label>
              <textarea
                id="inv-log-entry"
                className="sp-textarea w-full"
                rows={3}
                value={form.notesLog}
                onChange={(event) => setForm((prev) => ({ ...prev, notesLog: event.target.value }))}
              />
            </div>
          </div>
        </section>
      </div>

      <div className="sp-form-footer">
        <Button variant="secondary" onClick={() => navigate('/inventory')}>Cancel</Button>
        <Button onClick={handleSubmit}>Save Item</Button>
      </div>
    </Card>
  );
}
